import { useCallback, useMemo } from 'react';
import {
  SubscriptionStatus,
  PlanStatus,
  UserInfo,
  AiFeatureType,
  AiFeatureStatus,
  PlanQuota
} from 'types/user';
import { useUserStore } from 'contexts/useUserStore';

const AI_QUOTA_KEY_MAP: Record<AiFeatureType, keyof PlanQuota> = {
  ask_ai: 'ai_ask_ai',
  summarize: 'ai_summarize',
  expand: 'ai_expand',
  find_references: 'ai_find_references',
  generate_image: 'ai_generate_image'
};

const usePlanStatus = (): PlanStatus => {
  const { userInfo } = useUserStore();
  const {
    is_internal: isUserInternal = false,
    in_progress_documents: inProgressDocuments = 0,
    plan
  } = userInfo as UserInfo;
  const {
    type = '',
    end_date: endDate = null,
    cancel_at_period_end: cancelAtPeriodEnd = false,
    quota
  } = plan || {};

  const remainingDocuments = quota?.document?.remaining ?? null;
  const totalDocuments = quota?.document?.total ?? null;
  const showRemainingDocuments = quota?.document?.show_remaining ?? false;
  const documentPeriod = quota?.document?.period;
  const documentResetAt = quota?.document?.reset_at;
  const remainingDeepResearch = quota?.deep_research?.remaining ?? null;
  const totalDeepResearch = quota?.deep_research?.total ?? null;
  const showRemainingDeepResearch =
    quota?.deep_research?.show_remaining ?? false;
  const deepResearchPeriod = quota?.deep_research?.period;
  const deepResearchResetAt = quota?.deep_research?.reset_at;

  const isFree = type === 'free';
  const isAdvanced = type === 'advanced';
  const isPro = type === 'professional';
  const isMarketplaceUser = plan?.purchase_source === 'ms_marketplace';
  const cannotUpgrade = isMarketplaceUser || isPro;
  const isCanceled = cancelAtPeriodEnd === true;
  const hasNoRemainingDocuments = remainingDocuments === 0;
  const isReachedLimit = hasNoRemainingDocuments;
  const limitType = type;
  const isDeepResearchLocked = remainingDeepResearch === 0;

  const subscriptionStatus: SubscriptionStatus = useMemo(() => {
    if (isFree) return 'free';
    if (isCanceled) return 'canceled';
    return 'active';
  }, [isCanceled, isFree]);

  const getAiFeatureStatus = useCallback(
    (featureType: AiFeatureType): AiFeatureStatus => {
      const aiQuota = quota?.[AI_QUOTA_KEY_MAP[featureType]];
      return {
        isLocked: aiQuota?.remaining === 0,
        remaining: aiQuota?.remaining ?? null,
        total: aiQuota?.total ?? null,
        showRemaining: aiQuota?.show_remaining ?? false,
        period: aiQuota?.period,
        resetAt: aiQuota?.reset_at
      };
    },
    [quota]
  );

  return {
    isFree,
    isAdvanced,
    isPro,
    isCanceled,
    isReachedLimit,
    isLimited: !isUserInternal && isReachedLimit,
    isPlanReachedLimit: isAdvanced && hasNoRemainingDocuments,
    isMarketplaceUser,
    cannotUpgrade,
    subscriptionStatus,
    remainingDocuments,
    totalDocuments,
    inProgressDocuments,
    endDate,
    limitType,
    showRemainingDocuments,
    documentPeriod,
    documentResetAt,
    remainingDeepResearch,
    totalDeepResearch,
    showRemainingDeepResearch,
    deepResearchPeriod,
    deepResearchResetAt,
    isDeepResearchLocked,
    getAiFeatureStatus,
    limitInfos: {
      free: 'Editing documents is unavailable.'
    }
  };
};

export default usePlanStatus;
